import { select, take, put, takeEvery } from "redux-saga/effects";
import Logger from "../utils/logger";
import {
  isCloudPillowGWPEnabled,
  getAutoApplyDiscountCode,
} from "../utils/promos";
import {
  constructCtaProductData,
  getLineItemDataByVariantId,
} from "../utils/shop";
import {
  addToCartRequest,
  updateQuantityRequest,
  removeDiscountCodeRequest,
  addProductAsGiftWithPurchaseRequest,
  removeProductAsGiftWithPurchaseRequest,
  removeProductAsGiftWithPurchaseReceive,
} from "../actions/ShopActions";
import {
  getCheckoutId,
  getCartLineItems,
  getCheckoutState,
  getAllProducts,
  getGiftWithPurchaseProducts,
} from "../utils/reduxSelectors";

// Events
import * as Shop from "../types/Shop";

// Constants
import { RESTORE_KEY, HATCH_CLOUD_PILLOW_KEY } from "../utils/productConstants";
const TAG = "GiftWithPurchaseSaga";
const AUTO_APPLY_DISCOUNT_CODE = getAutoApplyDiscountCode();
const IS_CLOUD_PILLOW_PROMO_ENABLED = isCloudPillowGWPEnabled();

const GiftWithPurchaseSaga = () => {
  function* worker(action) {
    // alert('worker')
    Logger.d(TAG, `Processing action: ${action.type}`);

    const checkoutId = yield select(getCheckoutId) || null;
    /**
     *  GIFT WITH PURCHASE
     * ***************************************************
     */
    const allProducts = yield select(getAllProducts);
    const cloudPillowData = allProducts[HATCH_CLOUD_PILLOW_KEY];
    const isRestoreProduct = allProducts[RESTORE_KEY]
      ? allProducts[RESTORE_KEY].variants[0].id === action.variantId
      : false;
    // When a product is added to cart, check to see
    if (action.type === Shop.ADD_TO_CART_RECEIVE) {
      const allGWPProducts = yield select(getGiftWithPurchaseProducts);
      const discountCode = AUTO_APPLY_DISCOUNT_CODE || null;

      if (
        IS_CLOUD_PILLOW_PROMO_ENABLED &&
        discountCode &&
        isRestoreProduct &&
        !allGWPProducts[HATCH_CLOUD_PILLOW_KEY]
      ) {
        // Pillow is in the cart and no GWP one is present, just apply the discount
        const cartLineItems = yield select(getCartLineItems);
        const pillowLineItem = getLineItemDataByVariantId(
          cartLineItems,
          cloudPillowData.variants[0].id
        );

        if (!pillowLineItem) {
          const productData = constructCtaProductData(
            cloudPillowData,
            cloudPillowData.variantId
          );

          yield put(
            addToCartRequest({
              checkoutId,
              quantity: 1,
              variantId: productData.variantId,
              customAttributes: productData.customAttributes,
            })
          );

          const addFreePillowToCart = yield take([
            Shop.ADD_TO_CART_RECEIVE,
            Shop.ADD_TO_CART_REQUEST_FAILURE,
          ]);

          if (addFreePillowToCart.type === Shop.ADD_TO_CART_REQUEST_FAILURE) {
            return Logger.d(
              TAG,
              `Add free pillow to cart error: ${JSON.stringify(
                addFreePillowToCart.errors
              )}`
            );
          }
        }

        yield put(
          addProductAsGiftWithPurchaseRequest({
            checkoutId,
            discountCode,
            productKey: HATCH_CLOUD_PILLOW_KEY,
          })
        );

        const applyFreePillowDiscountCodeRequest = yield take([
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE,
        ]);

        if (
          applyFreePillowDiscountCodeRequest.type ===
          Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Add free pillow discount code error: ${JSON.stringify(
              applyFreePillowDiscountCodeRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, "Free pillow added successfully to cart");
      }
    }

    // When FREE Pillow is removed from cart
    if (action.type === Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST) {
      const isPillowProduct =
        cloudPillowData.variants[0].id === action.lineItem.variantId;

      if (isPillowProduct) {
        // First trigger removal of discount code
        yield put(
          removeDiscountCodeRequest(
            action.checkoutId,
            HATCH_CLOUD_PILLOW_KEY,
            action.lineItem.variantId
          )
        );

        const removePillowGWPDiscountRequest = yield take([
          Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
          Shop.REMOVE_DISCOUNT_CODE_FAILURE,
        ]);

        if (
          removePillowGWPDiscountRequest.type ===
          Shop.REMOVE_DISCOUNT_CODE_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Removing free pillow discount code error: ${JSON.stringify(
              removePillowGWPDiscountRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, `Removing discount code success!`);
      }
    }

    if (
      action.type === Shop.REMOVE_DISCOUNT_CODE_RECEIVE &&
      action.discountCode === HATCH_CLOUD_PILLOW_KEY
    ) {
      const cartLineItems = yield select(getCartLineItems);
      const lineItem = getLineItemDataByVariantId(
        cartLineItems,
        action.variantId
      );

      // Then update the pillow quantity
      // console.log('REMOVING FREE PILLo!!!', action)
      // Update quantity of line item
      yield put(
        updateQuantityRequest(checkoutId, lineItem.id, lineItem.quantity - 1)
      );

      const updatePillowQuantityRequest = yield take([
        Shop.UPDATE_QUANTITY_RECEIVE,
        Shop.UPDATE_QUANTITY_REQUEST_FAILURE,
      ]);

      if (
        updatePillowQuantityRequest.type ===
        Shop.UPDATE_QUANTITY_REQUEST_FAILURE
      ) {
        return Logger.d(
          TAG,
          `Removing free pillow error: ${JSON.stringify(
            updatePillowQuantityRequest.errors
          )}`
        );
      }

      const updatedCheckout = yield select(getCheckoutState);

      // Then update Shop.giftWithPurchaseProducts
      yield put(
        removeProductAsGiftWithPurchaseReceive(
          HATCH_CLOUD_PILLOW_KEY,
          updatedCheckout
        )
      );
      return Logger.d(TAG, "Removed free pillow successfully from cart");
    }

    // When Restore line item and there is a Free pillow
    // in the cart
    if (
      action.type === Shop.REMOVE_FROM_CART_RECEIVE &&
      IS_CLOUD_PILLOW_PROMO_ENABLED
    ) {
      if (isRestoreProduct) {
        yield put(
          removeProductAsGiftWithPurchaseRequest({
            checkoutId: action.checkout.id,
            lineItem: action.lineItemId,
            productKey: HATCH_CLOUD_PILLOW_KEY,
          })
        );

        const changedFreePillowToFullPriceRequest = yield take([
          Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
          Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE,
        ]);

        if (
          changedFreePillowToFullPriceRequest.type ===
          Shop.UPDATE_QUANTITY_REQUEST_FAILURE
        ) {
          return Logger.d(
            TAG,
            `Changing free pillow to full price error: ${JSON.stringify(
              changedFreePillowToFullPriceRequest.errors
            )}`
          );
        }

        return Logger.d(TAG, "Changing free pillow to full price success!");
      }
    }
  }

  function* watcher() {
    // alert('watcher')
    yield takeEvery(
      [
        Shop.ADD_TO_CART_RECEIVE,
        Shop.REMOVE_FROM_CART_RECEIVE,
        Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
        Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
        Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST,
      ],
      worker
    );
  }

  return {
    watcher,
    worker,
  };
};

export default GiftWithPurchaseSaga;
